import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { X } from 'lucide-react';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true,
  className,
  ...props
}) => {
  const sizeStyles = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl',
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-neutral-900/40 backdrop-blur-sm"
            style={{ zIndex: 9999 }}
            onClick={handleOverlayClick}
          >
            <div className="flex min-h-screen items-center justify-center p-4 sm:p-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                className={clsx(
                  // max-h in vh is the fallback; the inline dvh below overrides it
                  // on iOS Safari / mobile so the modal accounts for the dynamic
                  // URL bar + on-screen keyboard (fixes the submit button being
                  // pushed off-screen / the page jumping on tap).
                  'relative w-full bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl mx-auto max-h-[calc(100vh-2rem)]',
                  sizeStyles[size],
                  className
                )}
                style={{ zIndex: 10000, maxHeight: 'calc(100dvh - 2rem)' }}
                onClick={(e) => e.stopPropagation()}
                {...props}
              >
                {(title || showCloseButton) && (
                  <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
                    {title && (
                      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                        {title}
                      </h2>
                    )}
                    {showCloseButton && (
                      <button
                        onClick={onClose}
                        className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors duration-150 focus:outline-none rounded-xl p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        aria-label="Close modal"
                      >
                        <X size={20} strokeWidth={1.75} />
                      </button>
                    )}
                  </div>
                )}

                <div
                  className="px-6 py-5 overflow-y-auto max-h-[calc(100vh-10rem)]"
                  style={{ maxHeight: 'calc(100dvh - 10rem)', WebkitOverflowScrolling: 'touch' }}
                >
                  {children}
                </div>

                {footer && (
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100 dark:border-neutral-700">
                    {footer}
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Modal;
